(() => {
  const singletonKey = Symbol.for('eusari.policiesCenter');
  const existing = window[singletonKey];

  if (existing) {
    existing.scan(document);
    return;
  }

  const instances = new WeakMap();
  const roots = new Set();

  function ownerOf(panelId) {
    if (!panelId) return null;
    return [...roots].find((root) => root.querySelector(`[data-policy-panel][id="${CSS.escape(panelId)}"]`)) || null;
  }

  function activate(root, panelId, { historyMode = null, focus = false } = {}) {
    const instance = instances.get(root);
    if (!instance) return false;
    const panel = instance.panels.find((candidate) => candidate.id === panelId);
    if (!panel) return false;

    if (!instance.enhanced) {
      instance.tablist.setAttribute('role', 'tablist');
      instance.tablist.setAttribute('aria-orientation', 'vertical');
      instance.tabs.forEach((candidate) => {
        candidate.setAttribute('role', 'tab');
        candidate.setAttribute('aria-controls', candidate.hash.slice(1));
        candidate.setAttribute('aria-selected', 'false');
      });
      instance.panels.forEach((candidate) => {
        candidate.setAttribute('role', 'tabpanel');
        candidate.setAttribute('aria-labelledby', `policy-tab-${candidate.id.slice('policy-'.length)}`);
        candidate.tabIndex = 0;
      });
      root.classList.add('is-enhanced');
      instance.enhanced = true;
    }

    const tab = instance.tabs.find((candidate) => candidate.getAttribute('aria-controls') === panel.id);
    if (!tab) return false;

    instance.tabs.forEach((candidate) => {
      const active = candidate === tab;
      candidate.setAttribute('aria-selected', String(active));
      candidate.tabIndex = active ? 0 : -1;
    });
    instance.panels.forEach((candidate) => { candidate.hidden = candidate !== panel; });
    instance.select.value = panel.id;

    if (historyMode) {
      const url = new URL(window.location.href);
      url.hash = panel.id;
      history[`${historyMode}State`]({}, '', url);
    }
    if (focus) panel.focus({ preventScroll: true });
    return true;
  }

  function syncFromLocation() {
    const panelId = window.location.hash.slice(1);
    const owner = ownerOf(panelId);
    if (owner) activate(owner, panelId);
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    const tabs = [...root.querySelectorAll('[data-policy-tab]')];
    const panels = [...root.querySelectorAll('[data-policy-panel]')];
    const select = root.querySelector('[data-policy-select]');
    const tablist = root.querySelector('[data-policy-tabs]');
    if (!tabs.length || !panels.length || !select || !tablist) return;

    const controller = new AbortController();
    const { signal } = controller;
    instances.set(root, { controller, enhanced: false, panels, select, tablist, tabs });
    roots.add(root);

    tabs.forEach((tab, index) => {
      tab.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        activate(root, tab.hash.slice(1), { historyMode: 'push' });
      }, { signal });
      tab.addEventListener('keydown', (event) => {
        if (!instances.get(root)?.enhanced) return;
        let nextIndex;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === undefined) return;
        event.preventDefault();
        tabs[nextIndex].focus();
        activate(root, tabs[nextIndex].hash.slice(1), { historyMode: 'push' });
      }, { signal });
    });

    select.addEventListener('change', () => activate(root, select.value, { historyMode: 'push' }), { signal });

    activate(root, window.location.hash.slice(1));
  }

  function destroy(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.controller.abort();
    instances.delete(root);
    roots.delete(root);
    root.classList.remove('is-enhanced');
  }

  function scan(scope) {
    if (scope.matches?.('[data-policies-center]')) init(scope);
    scope.querySelectorAll?.('[data-policies-center]').forEach(init);

    const panelId = window.location.hash.slice(1);
    if (roots.size === 1 && !ownerOf(panelId) && (!panelId || panelId.startsWith('policy-'))) {
      const root = roots.values().next().value;
      const firstPanel = instances.get(root)?.panels[0];
      if (firstPanel) activate(root, firstPanel.id, { historyMode: panelId ? 'replace' : null });
    }
  }

  const singleton = { scan };
  window[singletonKey] = singleton;

  window.addEventListener('popstate', syncFromLocation);
  window.addEventListener('hashchange', syncFromLocation);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-policies-center]')) destroy(event.target);
    event.target.querySelectorAll?.('[data-policies-center]').forEach(destroy);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-policies-center]');
    if (!root) return;
    const blockId = CSS.escape(event.detail.blockId);
    const panel = root.querySelector(`[data-policy-panel][data-block-id="${blockId}"]`);
    if (panel && activate(root, panel.id)) panel.focus({ preventScroll: true });
  });

  scan(document);
})();
